import { forwardRef, useState, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Text from '../../Text';
import { QuestionCircleLine } from '../../icons';
import Tooltip from '../../tooltip';
import { Theme } from '../../../theme/types';

type ToggleProps = JSX.IntrinsicElements['input'] & {
    label?: string;
    showTooltip?: boolean;
    tooltipText?: string;
};

const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
    ({ showTooltip = false, tooltipText = '', label, ...otherProps }, ref) => {
        const [open, setOpen] = useState(false);
        const classes = useStyles();
        const theme = useTheme<Theme>();
        const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
        const tooltipAnchor = useRef(null);

        const onMouseEnter = () => {
            setOpen(true);
        };

        const onMouseLeave = () => {
            setOpen(false);
        };

        const onClick = () => {
            setOpen(state => !state);
        };

        const handlers = isDesktop
            ? {
                  onMouseEnter,
                  onMouseLeave,
              }
            : {
                  onClick,
              };

        return (
            <div className={classes.wrapper}>
                <label className={classes.root}>
                    <input
                        className={classes.checkbox}
                        type="checkbox"
                        data-testid="toggle"
                        ref={ref}
                        {...otherProps}
                    />
                    <span className={classes.switcher} aria-hidden="true"></span>
                    {label && (
                        <Text variant="t81" className={classes.label}>
                            {label}
                        </Text>
                    )}
                </label>
                {showTooltip && (
                    <Tooltip
                        text={tooltipText}
                        open={open}
                        setOpen={setOpen}
                        customBackgroundColor={theme.palette.greys['100']}
                        customTextColor={theme.palette.greys['500']}
                        anchorRef={tooltipAnchor}
                        paperClassName={classes.paper}
                        arrowClassName={classes.arrow}
                        disablePortals
                    >
                        <div className={classes.iconWrapper} ref={tooltipAnchor} {...handlers}>
                            <QuestionCircleLine className={classes.questionIcon} />
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    }
);

Toggle.displayName = 'Toggle';

const useStyles = makeStyles(
    ({ palette }) => ({
        wrapper: {
            display: 'flex',
            alignItems: 'center',
        },
        root: {
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
        },
        checkbox: {
            width: 0,
            opacity: 0,
            position: 'absolute',
            zIndex: -1,

            '&:checked + $switcher': {
                backgroundColor: '#70C8B0', // TODO: needs to be changed to color from palette
                borderColor: '#70C8B0', // TODO: needs to be changed to color from palette
                '&:before': {
                    backgroundColor: palette.greys[100],
                    transform: 'translateX(calc(100% + 2px))',
                },
            },

            '&[disabled]:not([readonly]) + $switcher': {
                borderColor: palette.greys[400],
                cursor: 'not-allowed',
                '&:before': {
                    backgroundColor: palette.greys[400],
                },
            },

            '&:hover + $switcher': {
                borderColor: palette.greys[500],
                '&:before': {
                    backgroundColor: palette.greys[500],
                },
            },

            '&:checked:hover + $switcher': {
                backgroundColor: '#AEEDD1', // TODO: needs to be changed to color from palette
                borderColor: '#AEEDD1', // TODO: needs to be changed to color from palette
            },

            '&:focus': {
                outline: 'none',
            },
            '&:focus-visible + $switcher': {
                boxShadow: `0 0 0 5px ${palette.greys[200]}`,
            },
        },
        label: {
            marginLeft: 8,
            userSelect: 'none',
            cursor: 'pointer',
        },
        switcher: {
            display: 'block',
            width: 26,
            height: 16,
            borderRadius: 36,
            cursor: 'pointer',
            position: 'relative',
            padding: 5,
            border: `1px solid ${palette.greys[700]}`,
            backgroundColor: palette.greys[100],
            transition: 'all 200ms ease',
            flexShrink: 0,

            '&:before': {
                content: `""`,
                display: 'block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: palette.greys[700],
                position: 'absolute',
                transition: 'all 200ms ease',
                top: `calc(50% - 3px)`,
                left: 5,
            },
        },
        questionIcon: {
            display: 'inline-block',
        },
        paper: {
            borderRadius: '1.2em',
            filter: 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.1))',
            '-webkit-filter': 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.1))',
            overflow: 'visible',
            width: 'max-content',
        },
        iconWrapper: {
            marginLeft: 6,
            display: 'flex',
        },
        arrow: {
            filter: 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.1))',
            '-webkit-filter': 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.1))',
            zIndex: 1,
            fill: palette.greys['100'],
        },
    }),
    { classNamePrefix: 'Toggle' }
);

export default Toggle;
